import { useTranslation } from 'react-i18next';
import BulkEditor from './BulkEditor';
import LocationAwareSearch from '../../../../components/LocationAwareSearch';
import FilterHotels from '../../../../components/FilterHotels';
import { isEqual } from 'lodash';
import { useSearchParams } from '../../../../hooks';
import { useAsync } from 'react-async';
import { getHotels } from 'api/node/hotels';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { KoobPagination } from '@koob/ui';
import { OrderColumnEnum, OrderDirectionEnum } from '@koob/enums';
import { getImageUrl } from '@koob/utils';
import { useKoobStore } from '../../../../store';
import useBulkEditExcludedIds from './useBulkEditExcludedIds';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

export default function BulkEditorHotels() {
  const { t } = useTranslation('toConnectionRequest');
  const [searchParams] = useSearchParams();
  const { search, kindsIds, searchLocation } = searchParams;

  const stars = Boolean(searchParams.stars) ? Number(searchParams.stars) : null;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(48);
  const [totalPages, setTotalPages] = useState();
  const kinds = Boolean(kindsIds)
    ? (kindsIds ?? []).map(({ value }) => value)
    : undefined;

  const toConnectionRequestModel = useKoobStore(
    (state) => state.toConnectionRequestModel,
  );
  const [showOnlySelected, setShowOnlySelected] = useState(
    toConnectionRequestModel ? true : null,
  );

  const excludedIds = useBulkEditExcludedIds({
    fieldName: 'toConnectionRequestHotels',
    showOnlySelected,
  });
  const excludedIdsKey = excludedIds?.join(',');

  const requestFilters = {
    kinds,
    ...(excludedIds && { excludedIds }),
    ...(searchLocation?.kind && { [searchLocation.kind]: [searchLocation.id] }),
    search,
    stars,
    perPage: perPage,
    page,
    direction: OrderDirectionEnum.DESC,
    order: OrderColumnEnum.UPDATED_AT,
    // KD-3547 — no state filter, so the bulk editor lists hotels of every
    // state like the experiences and templates tabs already did.
  };

  useEffect(() => {
    setPage(1);
  }, [search, searchLocation, kindsIds, stars, excludedIdsKey]);

  const { data: hotels, isLoading: loading } = useAsync({
    promiseFn: getHotels,
    filters: requestFilters,
    locale: 'en',
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.filters, prevProps.filters) ||
        !isEqual(props.locale, prevProps.locale)
      );
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });

  const totalCount = hotels?.headers?.xTotalCount;
  const hotelList = hotels?.data?.data ?? [];

  // Only the "selected" view filters in the browser: it lists the local
  // selection, which the server never sees. Server pages are shown as they
  // come, and the search mirrors the server's (name or KOOB id, KD-3530).
  const searchWords = useMemo(() => toSearchWords(search), [search]);
  const filterResult = useCallback(
    (item) => {
      if (stars && String(item.stars) !== String(stars)) return false;
      return matchesSearchWords(searchWords, [item.name], item.id);
    },
    [stars, searchWords],
  );

  return (
    <BulkEditor
      title={t('bulkEdit.editor.hotelsTitle')}
      filters={
        <>
          <LocationAwareSearch
            name="search"
            placeholder={t('misc:searchByNameOrId')}
          />

          <FilterHotels />
        </>
      }
      loading={loading}
      results={(hotelList || []).map((hotel) => ({
        // Raw numeric KOOB id: the request payload and the "#id" label both
        // use it, so the encoded koobId gid is not carried along.
        id: hotel.id,
        image: getImageUrl(hotel),
        name: hotel.displayName,
        description: hotel.address,
        stars: hotel.stars,
        state: hotel.state,
      }))}
      type="hotel"
      totalCount={
        excludedIds ? Number(totalCount ?? 0) + excludedIds.length : totalCount
      }
      filterResult={filterResult}
      onShowOnlySelectedChange={setShowOnlySelected}
    >
      {hotelList.length > 0 && (
        <KoobPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      )}
    </BulkEditor>
  );
}
